
import { Component, Prop, Emit, Watch } from 'vue-property-decorator';
import { City, CitySelectMenuItem, LocationData, Region, RegionCity } from "@/types/locations";
import IButton from "@/ui-components/IButton/IButton.vue";
import Checkbox from '@/ui-components/Checkbox/Checkbox.vue';
import { adSpaceTypesIcons } from '@/statics/ad-spaces';
import { AdSpaceType } from '@/types/adspaces';
import SelectCheckBox
  from '@/pages/shared/CampaignWizard/components/Locations/components/components/SelectCheckBox.vue';
import { cloneDeep, groupBy, orderBy } from 'lodash';
import { Callback, Tuple } from '@/types/misc';
import TextField from '@/ui-components/TextField/TextField.vue';
import Divider from '@/ui-components/Divider/Divider.vue';
import SelectMenuSearch from '@/ui-components/SelectMenuSearch/SelectMenuSearch.vue';
import { StyleWatcher } from '@/mixins/style-watcher';
import { mixins } from 'vue-class-component';
import { Route } from 'vue-router';
import { TranslateResult } from 'vue-i18n';

// Update the import
import { 
  
  queryToFilterCriteria, 
  validateFilterCriteria,
  filterCriteriaToQuery,
  } from '@/utils/locations';

@Component({
  components: {
    SelectMenuSearch,
    Divider,
    IButton,
    Checkbox,
    SelectCheckBox,
    TextField,
  }
})
export default class LocationSelects extends mixins(StyleWatcher) {
  @Prop({ required: true, default: () => [] }) locations!: LocationData[];
  @Prop({  }) closeSheet!: Callback<void, void>;

  public selectedCities: Array<CitySelectMenuItem["GEONAME_ID"]> = [];
  public selectedLocationTypes: string[] = [];
  public sheet: boolean = false;
  public selected: boolean = false;
  isRegionMenuOpenMap: Record<string, boolean> = {};
  searchText = '';
  focus = false;
  cachedCities: CitySelectMenuItem[] = [];
  searchResult: CitySelectMenuItem[] = [];
  
  // Mobile navigation state
  showingRegionCities = false;
  currentRegion: CitySelectMenuItem | null = null;
  
  //fields from mixing
  selector = '.layout.stepper.split-view.fill-height.row';
  styleProp = 'paddingRight';
  isRegionMenuHasScroll = false;

  get isMobileView() {
    return this.$vuetify.breakpoint.mdAndDown;
  }

  get rightValueForRegionMenu() {
    if (this.$vuetify.breakpoint.mdAndDown) {
      return '201px'
    }
    return Number(this.styleValue) + 258 + 'px'
  }

  // URL Query Parameter Methods
  private filtersToQuery(): Record<string, string> {
    return filterCriteriaToQuery({
      selectedCities: this.selectedCities,
      selectedLocationTypes: this.selectedLocationTypes
    });
  }

  private queryToFilters() {
    const criteria = queryToFilterCriteria(this.$route.query);
    const validatedCriteria = validateFilterCriteria(criteria, this.locations);
    
    this.selectedCities = validatedCriteria.selectedCities || [];
    this.selectedLocationTypes = validatedCriteria.selectedLocationTypes || [];
  }

  private updateUrlQuery() {
    const newQuery = this.filtersToQuery();

    // Only update if query actually changed to avoid unnecessary navigation
    const { lang } = this.$route.query

    if (JSON.stringify(newQuery) !== JSON.stringify(this.$route.query)) {
      this.$router.replace({
        query: { ...newQuery, lang }
      }).catch(() => {
        // Ignore navigation duplicate errors
      });
    }
  }

  // Watch for route changes (browser back/forward)
  @Watch('$route')
  private onRouteChanged(newRoute: Route, oldRoute: Route) {
    // Only restore if query actually changed and it's not from our own navigation
    if (newRoute.path === oldRoute.path && 
        JSON.stringify(newRoute.query) !== JSON.stringify(oldRoute.query)) {
      this.queryToFilters();
      // Use nextTick to ensure the update happens properly
      this.$nextTick(() => {
        this.onChangedFilters();
      });
    }
  }

  // Add a watcher for when locations data changes (in case it loads after component mount)
  @Watch('locations', { immediate: true })
  private onLocationsDataChanged() {
    // Only reapply if we have both locations data and filters from URL
    if (this.locations.length > 0 && 
        (this.selectedCities.length > 0 || this.selectedLocationTypes.length > 0)) {
      this.$nextTick(() => {
        this.onChangedFilters();
      });
    }
  }

  citiesSearch(searchText: string) {
    if (this.cachedCities.length === 0) {
      this.cachedCities = cloneDeep(this.cities);
    }
    const searchResult = this.cachedCities.filter((city) => {
      const searchRegex = new RegExp(searchText, 'i')
      return city.regionCities?.length === 0
        && (
          searchRegex.test(this.$t(city.DISPLAY_NAME.en).toString()) ||
          (city.DISPLAY_NAME.ar && searchRegex.test(city.DISPLAY_NAME.ar))
        )
    })
    this.searchResult = orderBy(searchResult, (city) => city.POPULATION || 0, 'desc')
  }

  getCityWithGeonameId(regionCities: CitySelectMenuItem[], geonameId: string) {
    return regionCities.find(regionCity => regionCity.GEONAME_ID === geonameId)
  }

  isCitySelected(geonameId: string): boolean {
    return this.selectedCities.includes(geonameId)
  }

  regionHasSomeCitiesSelected(regionCities: CitySelectMenuItem[]) {
    return regionCities.some((regionCity) => this.isCitySelected(regionCity.GEONAME_ID))
  }

  regionHasAllCitiesSelected(regionCities: CitySelectMenuItem[]) {
    return regionCities.every((regionCity) => this.isCitySelected(regionCity.GEONAME_ID))
  }

  // TODO: refactor to more concise logic
  get cities(): CitySelectMenuItem[] {
    const uniqueRegionCitiesRecord = this.locations.reduce((cities, location) => {
      return {
        ...cities,
        [location.CITY.GEONAME_ID]: {
          ...location.CITY,
          ...location.REGION && {
            region: location.REGION,
          }
        },

      }
    }, {} as Record<RegionCity["GEONAME_ID"], RegionCity>);

    let uniqueRegionsRecord = this.locations.reduce((regions, location) => {
      return {
        ...regions,
        ...location.REGION && {
          [location.REGION.GEONAME_ID]: {
            ...location.REGION,
          }
        },

      }
    }, {} as Record<Region["GEONAME_ID"], Region>);

    const groupedCitiesByRegion: Record<Region["GEONAME_ID"], RegionCity[]> =
      groupBy(Object.values(uniqueRegionCitiesRecord), (city) => city.region?.GEONAME_ID);

    const orderedRegionsByPopulation = orderBy(Object.values(uniqueRegionsRecord) as CitySelectMenuItem[],
      (region) => region.POPULATION || 0,
      'desc');

    const orderedRegionCitiesRecordByPopulation = Object.entries(groupedCitiesByRegion)
      .reduce((orderedMap, [regionGeonameId, cities]) => {
        return {
          ...orderedMap,
          [regionGeonameId]: orderBy(cities, (city) => city.POPULATION || 0, 'desc'),
        }
      }, {} as Record<Region["GEONAME_ID"], CitySelectMenuItem[]>);

    const orderedRegionsWithRegionCitiesByPopulation = orderedRegionsByPopulation.map((region) => {
      region.regionCities = orderedRegionCitiesRecordByPopulation[region.GEONAME_ID];
      return region;
    })

    orderedRegionsWithRegionCitiesByPopulation.forEach((region) => {
      this.isRegionMenuOpenMap = { ...this.isRegionMenuOpenMap, [region.GEONAME_ID]: false };
    })

    return orderedRegionsWithRegionCitiesByPopulation.concat(
      Object.values(uniqueRegionCitiesRecord as Record<City["GEONAME_ID"], CitySelectMenuItem>).map((city) => {
        city.regionCities = [];
        return city;
      }));
  }

  get brandNames(): string[] {
    const uniqueBrandsSet = this.locations.reduce(
      (brands, { BRAND_NAME }) => brands.add(BRAND_NAME),
      new Set() as Set<string>
    );
    return Array.from(uniqueBrandsSet).filter(Boolean);
  }

  locationTypeImage(itemLocationType: AdSpaceType): string {
    return adSpaceTypesIcons[itemLocationType.VAL]?.icon || '';
  }

  get locationType() {
    const uniqueLocationType = this.locations.reduce(
      (type, location) => ({
        ...type,
        [location.TYPE.VAL]: {
          VAL: location.TYPE.VAL,
          DISPLAY_NAME: this.$t(location.TYPE.VAL)
        }
      }),
      {} as Record<LocationData['TYPE']['VAL'], { VAL: string; DISPLAY_NAME: TranslateResult }>
    );
    return Object.values(uniqueLocationType);
  }

  get mobileDisplayItems() {
    if (!this.isMobileView) {
      return this.cities;
    }
    
    if (this.showingRegionCities && this.currentRegion) {
      // Return only actual cities - clean!
      return this.currentRegion.regionCities || [];
    }
    
    return this.cities;
  }

  mounted() {
    // First restore filters from URL
    this.queryToFilters();
    // Then apply the filters with proper timing
    this.$nextTick(() => {
      this.onChangedFilters();
    });
  }

  public onChangedFilters(): void {
    // Only update URL, don't filter data
    this.updateUrlQuery();
    
    // Don't emit filtered data anymore - parent will handle filtering
  }

  clearAllFilters() {
    this.selectedCities = [];
    this.selectedLocationTypes = [];

    // Clear URL query as well but keep lang param
    const { lang } = this.$route.query;
    this.$router.replace({
      query: lang ? { lang } : {}
    }).catch(() => {
    });
    // Remove the filtering call - just update URL
    this.updateUrlQuery();
  }

  toggleCitySelect(geonameId: string) {
    if (this.isCitySelected(geonameId)) {
      this.selectedCities = this.selectedCities.filter((cityId) => cityId !== geonameId)
    } else {
      this.selectedCities.push(geonameId)
    }
    this.onChangedFilters();
  }

  toggleAllRegionCities([geonemeId, regionCities]: Tuple<string, CitySelectMenuItem[]>) {
    if (!this.regionHasAllCitiesSelected(regionCities)) {
      this.isRegionMenuOpenMap[geonemeId] = true;
      regionCities.forEach((regionCity) => {
        if (!this.isCitySelected(regionCity.GEONAME_ID)) {
          this.selectedCities = this.selectedCities.concat(regionCity.GEONAME_ID)
        }
      })
    } else {
      this.selectedCities = this.selectedCities.filter((geonameId) => !this.getCityWithGeonameId(regionCities, geonameId))

    }
    this.onChangedFilters()
  }

// TODO: refactor isRegionMenuHasScroll logic.
  @Watch('isRegionMenuOpenMap', { deep: true })
  onCitiesChanged() {
    const isLtrLanguage = this.$i18n.locale === 'en';
    const element = document.querySelector('.region-menu')
    if (isLtrLanguage && element) {
      this.isRegionMenuHasScroll = element.scrollHeight > element.clientHeight;
    } else {
      this.isRegionMenuHasScroll = false;
    }
  }

  showRegionCities(region: CitySelectMenuItem) {
    this.currentRegion = region;
    this.showingRegionCities = true;
    // Keep the menu open when navigating
    this.$nextTick(() => {
      // Force the select to stay open
      const selectComponent = this.$refs.citySelect as any;
      if (selectComponent && selectComponent.isMenuActive === false) {
        selectComponent.isMenuActive = true;
      }
    });
  }

  backToRegions() {
    this.showingRegionCities = false;
    this.currentRegion = null;
    // Keep the menu open when going back
    this.$nextTick(() => {
      const selectComponent = this.$refs.citySelect as any;
      if (selectComponent && selectComponent.isMenuActive === false) {
        selectComponent.isMenuActive = true;
      }
    });
  }

  handleRegionCheckboxClick(event: Event | undefined, geonameId: string, regionCities: CitySelectMenuItem[]) {
    if (event) event.stopPropagation();
    
    // Select/deselect all cities in region
    this.toggleAllRegionCities([geonameId, regionCities]);
    
    // Navigate to cities view to show what was selected
    if (this.isMobileView) {
      this.showRegionCities(this.cities.find(c => c.GEONAME_ID === geonameId) as CitySelectMenuItem);
    }
  }
}
